import {format, parse} from 'date-fns';
import Environment from 'utils/Environment';
import {SurveyData, STORAGE_ITEM_KEY} from 'screens/InsomniaSurvey/utils/surveyData';
import Analytics from 'utils/Analytics';
import {isTokenExpired} from 'utils/tokenUtils';
import {
  DiscoveryCheck,
  NewAsyncPatientRequest,
  NewAsyncPatientResponse,
  StartDiscoveryInput,
  StartDiscoveryResponse,
} from 'sleepioRx/types';

const env = Environment.getVar('REACT_APP_ENV') ?? 'test';

// API_URL and API_KEY are now handled by the Netlify function proxy

type Slot = {
  avatarUrl?: string;
  credentials?: string;
  providerId: number;
  time: string;
};

type Slots = {
  [date: string]: Slot[];
};

export type Schedule = {
  slots: Slots;
  /** Set when no provider matched the state/payer, e.g. "No providers available". */
  error?: string;
  details?: string;
};

export type Authenticate = {
  accessToken?: string;
  error?: string;
  expiresIn?: number;
  tokenType?: string;
  expiresAt?: string;
};

type ScheduleAppointmentPayload = {
  personId: any;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth?: string;
  time: string;
  providerId: number;
  timezone: string;
  state?: string;
  memberId?: string;
  payerName?: string;
  gender?: string;
  insuranceStatus?: string;
  ref?: string;
  bookingSource: 'web' | 'app';
};

type CreatePatientPayload = {
  personId: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  timezone: string;
  state?: string;
  dateOfBirth?: string;
  gender?: string;
  memberId?: string;
  payerName?: string;
  bookingSource: 'web' | 'app';
  ref?: string;
};

export type CreatedPatient = {
  personId?: number;
  patientId?: number;
  healthiePatientId?: number | null;
  /** Set when the record saved but the Healthie chart could not be created. */
  healthieError?: string | null;
  error?: string;
};

export type Appointment = {
  appointmentId: number;
  avatarUrl?: string;
  credentials?: string;
  providerId: number;
  providerName: string;
  patientId: number;
  error?: string;
  zoomJoinUrl?: string;
  status?: string;
  startTime?: string;
  endTime?: string;
};

export type MetricResponse = {
  id: string;
  category: string;
  value: number;
  createdAt: string;
  error?: string;
};

export type Verification = {
  verificationId?: string;
  error?: string;
};

export type Status = {
  success?: boolean;
  error?: string;
};

export type InsuranceInfo = {
  estimatedCost?: number;
  status?: string;
  statusUpdatedAt?: string;
  riseEligibilityStatus?: string;
  cardUploadStatus?: string;
  cardUploadStatusUpdatedAt?: string;
  payerName?: string;
  state?: string;
  memberId?: string;
  dob?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
};

export type Appointments = {
  insuranceInfo?: InsuranceInfo;
  appointments: Appointment[];
  tags?: string[];
};

export type AppointmentReschedule = {
  appointmentId: number;
  avatarUrl?: string;
  credentials?: string;
  newTime: string;
  providerId: number;
  providerName: string;
  error?: string;
  zoomJoinUrl?: string;
};

export function normalizeYearToFourDigits(year: number): number {
  let fourDigitYear = year;
  let yearString = year.toString();
  if (year >= 0 && year < 10) {
    yearString = yearString.padStart(2, '0');
  }
  const currentYear = new Date().getFullYear();
  try {
    if (yearString.length === 2 && yearString !== '19' && yearString !== '20') {
      if (year > currentYear - 2000) {
        fourDigitYear = year + 1900;
      } else {
        fourDigitYear = year + 2000;
      }
    }
  } catch (e) {}
  return fourDigitYear;
}

export function getNormalizedDate(date: string): string {
  const [month, day, year] = date.split('/');
  const normalizedYear = normalizeYearToFourDigits(Number(year));
  return `${month}/${day}/${normalizedYear}`;
}

export function convertDateToAPIFormat(dateString: string, dateFormat: string): string {
  const normalizedDateString = getNormalizedDate(dateString);
  const parsedDate = parse(normalizedDateString, 'MM/dd/yyyy', new Date());
  return format(parsedDate, dateFormat);
}

// Query string conversion no longer needed with proxy approach

function request<Type>(
  endpoint: string,
  method: 'POST' | 'GET' | 'PATCH',
  data?: Record<string, unknown>,
  token?: string,
  headers?: Record<string, string>,
) {
  return new Promise<Type>((resolve, reject) => {
    const proxyHeaders: Record<string, string> = headers ?? {
      'Content-Type': 'application/json',
    };
    if (token) {
      proxyHeaders.Authorization = `Bearer ${token}`;
    }

    // Ensure data is JSON-serializable (not FormData)
    if (data && data instanceof FormData) {
      reject(new Error('FormData cannot be passed directly to request(). Use uploadInsuranceCardWeb() instead.'));
      return;
    }

    fetch('/.netlify/functions/insurance-eligibility-proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        method,
        data,
        headers: proxyHeaders,
        env,
      }),
    })
      .then(async response => {
        const responseData = await response.json();
        if (!response.ok) {
          const errorMsg =
            responseData?.error ?? 'Insurance Eligibility: An unexpected error occurred. Please try again.';
          throw new Error(errorMsg);
        }
        return responseData as Type;
      })
      .then((data: Type) => resolve(data))
      .catch(error => {
        reject(error);
      });
  });
}

// API configuration is now handled by the Netlify function proxy

const getTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const startVerification = (
  personId: number,
  firstName: string,
  lastName: string,
  dateOfBirth: string,
  state: string,
  memberId: string,
  payerName: string,
  phone: string,
  email: string,
  token: string,
) =>
  request<Verification>(
    'start-verification',
    'POST',
    {
      personId,
      firstName,
      lastName,
      dateOfBirth: convertDateToAPIFormat(dateOfBirth, 'MM/dd/yyyy'),
      state,
      memberId,
      payerName,
      phone,
      email,
      metadata: {
        isDev: env !== 'production',
      },
      timezone: getTimezone(),
    },
    token,
  );

export const getAppointmentAvailability = (state: string, payerName: string, personId?: number) =>
  request<Schedule>('healthie/appointment-availability', 'POST', {
    personId: personId ?? 0,
    state,
    payerName,
    timezone: getTimezone(),
  });

/**
 * Persist the patient (and their Healthie chart) without booking anything.
 *
 * Called when provider availability comes back empty: the patient has already
 * given us everything an appointment needs, so we save the record and hand them
 * off to the care team rather than dropping the intake on the floor.
 */
export const createPatientRecord = (
  personId: number,
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  token: string,
  options: {
    state?: string;
    dateOfBirth?: string;
    gender?: string;
    memberId?: string;
    payerName?: string;
    ref?: string;
  } = {},
) => {
  const payload: CreatePatientPayload = {
    personId,
    firstName,
    lastName,
    email,
    phoneNumber,
    timezone: getTimezone(),
    bookingSource: 'web',
  };
  if (options.state) payload.state = options.state;
  if (options.gender) payload.gender = options.gender;
  if (options.memberId) payload.memberId = options.memberId;
  if (options.payerName) payload.payerName = options.payerName;
  if (options.ref) payload.ref = options.ref;
  if (options.dateOfBirth) {
    payload.dateOfBirth = convertDateToAPIFormat(options.dateOfBirth, 'MM/dd/yyyy');
  }

  return request<CreatedPatient>('healthie/create-patient', 'POST', payload, token);
};

// POST /auth/authenticate - mint a booking token for this person.
// `hypnosToken` is the Hypnos JWT from /api/web-create-account. eligibility-check checks it
// against Hypnos GET /api/whoami and, once enforcing, mints only for the person it belongs to
// (RET-3230). It is omitted when absent so older survey data keeps working during the rollout.
export const authenticate = (email: string, personId: number, hypnosToken?: string | null) =>
  request<Authenticate>('auth/authenticate', 'POST', {
    personId,
    email,
    ...(hypnosToken ? {hypnosToken} : {}),
  });

/**
 * Check if the eligibility token is expired or invalid.
 * Uses a 5-minute buffer to refresh before actual expiration.
 */
export const isEligibilityTokenExpired = (token: string | null | undefined): boolean => isTokenExpired(token);

/**
 * Refresh the eligibility token by re-calling authenticate with stored email and person_id.
 * Updates localStorage with the new token.
 *
 * @returns The fresh token, or null if refresh failed.
 */
export const refreshEligibilityToken = async (): Promise<string | null> => {
  const surveyData = SurveyData.read();
  const email = surveyData?.email;
  const personId = surveyData?.person_id;
  const hypnosToken = surveyData?.hypnos_token;

  if (!email || !personId) {
    console.error('Cannot refresh eligibility token: missing email or person_id in survey data');
    return null;
  }

  console.log('Eligibility token expired or invalid, refreshing...');
  Analytics.track('Eligibility Token Refresh Started', {email});

  try {
    const response = await authenticate(email, personId, hypnosToken);

    if (response.error || !response.accessToken) {
      console.error('Failed to refresh eligibility token:', response.error);
      Analytics.track('Eligibility Token Refresh Failed', {email, error: response.error});
      return null;
    }

    // Update localStorage with fresh token
    const currentData = SurveyData.read() ?? {};
    const updatedData = {...currentData, eligibility_token: response.accessToken};
    window.localStorage.setItem(STORAGE_ITEM_KEY, JSON.stringify(updatedData));

    console.log('Eligibility token refreshed successfully');
    Analytics.track('Eligibility Token Refresh Success', {email});

    return response.accessToken;
  } catch (error) {
    console.error('Failed to refresh eligibility token:', error);
    Analytics.track('Eligibility Token Refresh Failed', {email, error: (error as Error).message});
    return null;
  }
};

// POST: /auth/magic-link/verify - Verify a magic link code and return a verified JWT
// Request payload:
//   email: string (ie. 'user@example.com')
//   code: string (ie. 'abc123...')
// Successful Response - status 200:
//   {
//     "accessToken": "eyJ...",
//     "expiresIn": 86400,
//     "tokenType": "Bearer",
//     "expiresAt": "2025-11-21T22:30:00Z"
//   }
export const verifyMagicLink = (email: string, code: string) =>
  request<Authenticate>('auth/magic-link/verify', 'POST', {
    email,
    code,
  });

// POST: /auth/magic-link/send - Generate and send a magic link for passwordless authentication
// Request payload:
//   email: string (ie. 'user@example.com')
// Successful Response - status 200:
//   {
//     "success": true,
//     "message": "Magic link sent to your email",
//     "_debug": {
//       "code": "abc123...",
//       "expiresAt": "2025-11-21T22:30:00+00:00"
//     }
//   }
// Note: _debug field is only included in non-production environments
export type SendMagicLinkResponse = {
  success?: boolean;
  message?: string;
  error?: string;
  _debug?: {
    code: string;
    expiresAt: string;
  };
};

export const sendMagicLink = (email: string) =>
  request<SendMagicLinkResponse>('auth/magic-link/send', 'POST', {
    email,
  });

export const scheduleAppointment = (
  personId: number,
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  time: string,
  providerId: number,
  state?: string,
  memberId?: string,
  payerName?: string,
  dateOfBirth?: string,
  gender?: string,
  insuranceStatus?: string,
  token?: string,
  referralCode?: string,
) => {
  let scheduleAppointmentPayload: ScheduleAppointmentPayload = {
    personId,
    firstName,
    lastName,
    email,
    phoneNumber,
    time,
    providerId,
    timezone: getTimezone(),
    gender,
    insuranceStatus,
    bookingSource: 'web',
  };
  if (state && memberId && payerName) {
    scheduleAppointmentPayload = {
      ...scheduleAppointmentPayload,
      state,
      memberId,
      payerName,
    };
  }
  if (dateOfBirth) {
    scheduleAppointmentPayload = {
      ...scheduleAppointmentPayload,
      dateOfBirth: convertDateToAPIFormat(dateOfBirth, 'yyyy-MM-dd'),
    };
  }
  if (referralCode) {
    scheduleAppointmentPayload = {
      ...scheduleAppointmentPayload,
      ref: referralCode,
    };
  }
  return request<Appointment>('healthie/schedule-appointment', 'POST', scheduleAppointmentPayload, token);
};

export const updateMetric = (
  personId: number,
  patientId: number,
  category: string,
  value: number,
  createdAt: string,
  token: string,
) =>
  request<MetricResponse>(
    `healthie/update-metric`,
    'POST',
    {
      personId,
      patientId,
      category,
      value,
      createdAt,
    },
    token,
  );

// Cancel an appointment
// Request payload:
// personId: int (ie. 1234)
// appointmentId: int(ie. 1234)
export const cancelAppointment = (personId: number, appointmentId: number, token?: string) =>
  request<Status>(
    `healthie/cancel-appointment`,
    'POST',
    {
      personId,
      appointmentId,
    },
    token,
  );

// Reschedule an existing appointment
// Request payload:
// personId: int (ie. 1234)
// appointmentId: int(ie. 1234)
// providerId: int (ie. 123456)
// newTime: string (ie. '2024-09-21T16:00:00+0000')
export const rescheduleAppointment = (
  personId: number,
  appointmentId: number,
  providerId: number,
  newTime: string,
  token?: string,
) =>
  request<AppointmentReschedule>(
    `healthie/reschedule-appointment`,
    'POST',
    {
      personId,
      appointmentId,
      providerId,
      newTime,
    },
    token,
  );

// Get all appointments for a specific patient
// Request payload:
// personId: int (ie. 1234)
export const getScheduledAppointments = (personId: number, token?: string) =>
  request<Appointments>(
    `healthie/patient-appointments`,
    'POST',
    {
      personId,
    },
    token,
  );

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Web-compatible version that accepts File objects
export const uploadInsuranceCardWeb = async (
  personId: number,
  insuranceCardFront?: File,
  insuranceCardBack?: File,
  token?: string,
) => {
  const data: Record<string, any> = {
    personId: personId.toString(),
  };

  // Convert files to base64 and add to data
  if (insuranceCardFront) {
    const base64 = await fileToBase64(insuranceCardFront);
    data.insuranceCardFront = {
      base64,
      filename: insuranceCardFront.name,
      contentType: insuranceCardFront.type,
    };
  }

  if (insuranceCardBack) {
    const base64 = await fileToBase64(insuranceCardBack);
    data.insuranceCardBack = {
      base64,
      filename: insuranceCardBack.name,
      contentType: insuranceCardBack.type,
    };
  }

  return request<Status>('healthie/upload-insurance-card', 'POST', data, token);
};

// Mailing address (CMS-1500 Box 5) for insurance billing. Must be called AFTER
// sleepio/intake — intake creates the Patient record; the backend pushes the
// address to Healthie server-side. Auth is the same non-verified eligibility_token
// the funnel already holds; the token's personId must match the body's personId.
export const updatePatientAddressWeb = (
  personId: number,
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
  },
  token: string,
) => request<Status>('healthie/update-address', 'POST', {personId, ...address}, token);

export const submitSleepioIntake = (data: NewAsyncPatientRequest, token: string) =>
  // bookingSource tags the patient as a web user (Patient.booking_source) — clinic-web
  // is inherently 'web', same assertion as metadata.surface / startDiscovery's patientType.
  request<NewAsyncPatientResponse>(
    'sleepio/intake',
    'POST',
    {...data, bookingSource: 'web'} as unknown as Record<string, unknown>,
    token,
  );

// ----- Sohar insurance discovery (mirrors nyx services/SleepioRx.ts) -----

const DISCOVERY_TIMEOUT = 12 * 1000; // 12s overall budget
const DISCOVERY_INTERVAL = 1.5 * 1000; // 1.5s between status checks

// POST /start-discovery — @unified_auth_required, so the unverified web JWT works.
// Forward phone/email/zipCode: the backend upserts the patient and assigns every
// provided field, so omitting them would null stored contact info. patientType is
// 'sleepio' and bookingSource is 'web' (backend stamps Patient.patient_type /
// booking_source from these — this is the earliest upsert, so the patient is tagged
// from creation). `dateOfBirth` accepts the funnel's masked value;
// convertDateToAPIFormat normalizes it to MM/dd/yyyy.
export const startDiscovery = (input: StartDiscoveryInput, token: string) =>
  request<StartDiscoveryResponse>(
    'start-discovery',
    'POST',
    {
      personId: input.personId,
      firstName: input.firstName,
      lastName: input.lastName,
      dateOfBirth: convertDateToAPIFormat(input.dateOfBirth, 'MM/dd/yyyy'),
      state: input.state,
      phone: input.phone,
      email: input.email,
      zipCode: input.zipCode,
      patientType: 'sleepio',
      bookingSource: 'web',
      timezone: getTimezone(),
      metadata: {isDev: env !== 'production'},
    },
    token,
  );

// GET /check-discovery?personId=… — @unified_auth_required; forward the user's
// eligibility JWT. personId is baked into the query string because the proxy
// drops the body on GET. A 404 (not-yet-indexed) is surfaced as `pending` so
// the poll keeps going.
export const checkDiscovery = async (personId: number, token: string): Promise<DiscoveryCheck> => {
  const response = await fetch('/.netlify/functions/insurance-eligibility-proxy', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      endpoint: `check-discovery?personId=${personId}`,
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`},
      env,
    }),
  });

  if (response.status === 404) {
    return {status: 'pending', found: false};
  }

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error ?? `Discovery check failed: ${response.status}`);
  }
  return body as DiscoveryCheck;
};

// Polls check-discovery until it resolves, the signal aborts, or the budget is hit.
// Callers should treat `status === 'complete' && found` as the only "use the
// discovered policy" path and fall back to manual member-ID entry otherwise.
export const pollForDiscovery = async (
  personId: number,
  token: string,
  signal?: AbortSignal,
): Promise<DiscoveryCheck> => {
  let timeElapsed = 0;
  while (timeElapsed < DISCOVERY_TIMEOUT) {
    if (signal?.aborted) {
      return {status: 'cancelled', found: false};
    }
    const run = await checkDiscovery(personId, token);
    if (!run.status || run.status === 'pending') {
      await new Promise(resolve => setTimeout(resolve, DISCOVERY_INTERVAL));
      timeElapsed += DISCOVERY_INTERVAL;
    } else {
      return run;
    }
  }
  return {status: 'error.timed.out', found: false};
};

// Send Slack notification for new clinic booking
export const sendSlackNotification = async (surveyData: any, appointment: any) => {
  return fetch('/.netlify/functions/slack-notification', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      surveyData,
      appointment,
    }),
  })
    .then(async response => {
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.error ?? 'Failed to send Slack notification');
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error sending Slack notification:', error);
      throw error;
    });
};
